import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useGeolocation } from "../hooks/useGeolocation";
import { useLocationRefinement } from "../hooks/useLocationRefinement";
import { SosButton } from "../components/SosButton";
import { AlertResultModal } from "../components/AlertResultModal";
import { InstallBanner } from "../components/InstallBanner";
import { createAlert } from "../lib/api";
import { toast } from "sonner";
import { ChevronRight, Lock, PlayCircle, CheckCircle2 } from "lucide-react";
import { useT, lang } from "../lib/lang";
import { useSilentMode } from "../lib/useSilentMode";
import { vibrate } from "../lib/silent";
import { BroadcastBanner } from "../components/BroadcastBanner";
import { EMERGENCY_TYPES } from "../components/EmergencyTypeGrid";
import { TutorialModal } from "../components/TutorialModal";
import { HomeWatermark } from "../components/home/HomeWatermark";
import { HomeHeader } from "../components/home/HomeHeader";
import { HomeReviewScreen } from "../components/home/HomeReviewScreen";
import { HomeTypeDrawer } from "../components/home/HomeTypeDrawer";
import { HomeEvidenceDrawer } from "../components/home/HomeEvidenceDrawer";

const VALID_TYPES = EMERGENCY_TYPES.map((t) => t.key);

export default function HomePage() {
  const { coords, refresh } = useGeolocation();
  const { arm: armLocationRefinement } = useLocationRefinement(coords);
  const location = useLocation();
  const navigate = useNavigate();
  // PWA shortcut deep-link support: /?type=Medical — read URL ONCE at
  // mount via a lazy state initializer.
  const [types, setTypes] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const requested = params.get("type");
      if (requested && VALID_TYPES.includes(requested)) return [requested];
    } catch (err) {
      console.warn("[HomePage] failed to read ?type= URL param:", err?.message || err);
    }
    return [];
  });
  const [description, setDescription] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  // When user long-pressed a spot on the Carte tab, MapPage navigates here
  // with `state.reportFrom = {latitude, longitude, accuracy, source}`. We
  // use those coords instead of live GPS for this dispatch only.
  const [reportFrom, setReportFrom] = useState(() => location.state?.reportFrom || null);
  // Stepped flow: idle → pickType → pickEvidence → review → (sending → idle)
  // The home SOS button now uses a single-tap "instant" mode and only
  // *opens* the picker. The final dispatch happens exclusively from the
  // review screen's "ENVOYER L'ALERTE MAINTENANT" button.
  const [flowStep, setFlowStep] = useState("idle");
  const [tutorialOpen, setTutorialOpen] = useState(false);
  const [silent] = useSilentMode();
  const [t, labels, currentLang] = useT();

  // First-time onboarding: auto-open the French tutorial on the very first
  // visit, then never again. Persisted via `rr_tutorial_seen=true`.
  useEffect(() => {
    try {
      const seen = window.localStorage.getItem("rr_tutorial_seen") === "true";
      if (seen) return undefined;
      const timer = setTimeout(() => setTutorialOpen(true), 600);
      return () => clearTimeout(timer);
    } catch (err) {
      console.error("tutorial seen flag read failed", err);
      return undefined;
    }
  }, []);

  const handleTutorialClose = () => {
    setTutorialOpen(false);
    try { window.localStorage.setItem("rr_tutorial_seen", "true"); }
    catch (err) { console.error("tutorial seen flag write failed", err); }
  };

  const cycleLang = () => {
    const order = ["fr", "en"];
    const next = order[(order.indexOf(currentLang) + 1) % order.length];
    lang.set(next);
  };

  // SOS tap (single tap, instant mode): open the type picker. The home
  // button never dispatches an alert — review screen does.
  const handleSosTap = () => {
    if (!coords && !reportFrom) {
      if (!silent) toast.error(t.waitingGps);
      else vibrate([100, 80, 100]);
      try { refresh(); } catch (err) { console.error("gps refresh", err); }
      return;
    }
    setFlowStep("pickType");
  };

  const onTypeContinue = () => {
    if (types.length === 0) {
      toast.error(t.noTypeSelected || "Sélectionnez un type d'urgence");
      return;
    }
    setFlowStep("pickEvidence");
  };

  const onEvidenceContinue = () => setFlowStep("review");

  const clearReportFrom = () => {
    setReportFrom(null);
    if (location.state?.reportFrom) {
      navigate(location.pathname, { replace: true, state: {} });
    }
  };

  const dispatchAlert = async () => {
    // Iter69 — Prefer the rolling 30-s "best fix" over the latest
    // sample when both exist. Falls back to the latest sample then to
    // a map-selected `reportFrom` point (which is now never set since
    // long-press alert creation was removed in Iter68 — preserved
    // here for forward compat only).
    const liveCoords = reportFrom || coords?.best || coords;
    if (!liveCoords) {
      toast.error(t.waitingGps);
      return;
    }
    if (types.length === 0) {
      toast.error(t.noTypeSelected || "Sélectionnez un type d'urgence");
      setFlowStep("pickType");
      return;
    }
    setSubmitting(true);
    try {
      const payloadBase = {
        description: reportFrom
          ? `[Signalé depuis la carte] ${description}`.trim()
          : description,
        coordinates: {
          latitude: liveCoords.latitude,
          longitude: liveCoords.longitude,
          accuracy: liveCoords.accuracy,
          altitude: liveCoords.altitude,
          altitude_accuracy: liveCoords.altitudeAccuracy,
          heading: liveCoords.heading,
          speed: liveCoords.speed,
          // Iter69 — Forward the GPS fix timestamp so Central Admin
          // can see if the report was sent with a fresh fix or a
          // stale cached one. `pos.timestamp` is ms since epoch.
          timestamp: liveCoords.timestamp,
        },
        notify_contacts: true,
        attachments,
      };
      const settled = await Promise.all(
        types.map((ty) =>
          createAlert({ ...payloadBase, emergency_type: ty })
            .then((alert) => ({ ok: true, ty, alert }))
            .catch((err) => ({ ok: false, ty, err }))
        )
      );
      const successes = settled.filter((s) => s.ok);
      const failures = settled.filter((s) => !s.ok);
      const canonical = successes[0]?.alert;
      // Iter90 — GPS audit: the SOS already went out with `liveCoords`
      // (best-available-now). Keep watching for up to 60 s and push
      // any strictly-better fix onto these SAME alert id(s) — never
      // creates a duplicate alert.
      if (successes.length > 0) {
        armLocationRefinement(successes.map((s) => s.alert?.id), liveCoords.accuracy);
      }
      if (silent) {
        vibrate(successes.length > 0 ? 200 : [100, 80, 100]);
      } else if (successes.length === 0) {
        const first = failures[0]?.err?.response?.data?.detail || t.failedSend;
        toast.error(first);
      } else {
        setResult(canonical);
        const okLabels = successes.map((s) => labels[s.ty]?.label || s.ty).join(" · ");
        toast.success(`${t.alertDispatched} · ${okLabels}`);
      }
      setDescription("");
      setAttachments([]);
      setTypes([]);
      clearReportFrom();
      setFlowStep("idle");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="relative flex-1 flex flex-col bg-background text-foreground overflow-hidden"
      data-testid="home-page"
    >
      <HomeWatermark />

      <InstallBanner />
      <BroadcastBanner coords={coords} />

      {reportFrom && (
        <div
          className="relative z-20 mx-3 mt-2 rounded-xl border-2 border-mali-red/60 bg-mali-red/5 px-3 py-2 flex items-center gap-2"
          data-testid="report-from-banner"
        >
          <span className="w-2 h-2 rounded-full bg-mali-red animate-pulse shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-mali-red leading-tight">
              Signalement depuis la carte
            </div>
            <div className="font-mono text-[10px] text-muted-foreground truncate">
              {reportFrom.latitude.toFixed(5)}, {reportFrom.longitude.toFixed(5)}
            </div>
          </div>
          <button
            type="button"
            onClick={clearReportFrom}
            data-testid="report-from-banner-clear"
            className="text-[10px] font-bold uppercase tracking-[0.12em] text-mali-red hover:underline"
          >
            Utiliser ma position
          </button>
        </div>
      )}

      <HomeHeader tagline={t.appTagline} currentLang={currentLang} onCycleLang={cycleLang} />

      <main className="relative z-10 flex-1 flex flex-col px-5 pt-4 pb-4 gap-3 max-w-md mx-auto w-full">
        {/* 1 — Position status pill */}
        <div className="flex items-center justify-center" data-testid="position-status">
          {coords ? (
            <div className="inline-flex items-center gap-2 bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 rounded-full px-3 py-1">
              <span className="relative flex w-2 h-2">
                <span className="absolute inline-flex w-full h-full rounded-full bg-emerald-500 opacity-70 animate-ping" />
                <span className="relative inline-flex w-2 h-2 rounded-full bg-emerald-500" />
              </span>
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-700 dark:text-emerald-400" strokeWidth={2.5} />
              <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-emerald-800 dark:text-emerald-300">
                {t.positionDetected}
              </span>
            </div>
          ) : (
            <div className="inline-flex items-center gap-2 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 rounded-full px-3 py-1">
              <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
              <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-amber-800 dark:text-amber-300">
                {t.positionSearching}
              </span>
            </div>
          )}
        </div>

        {/* 2 — SOS button (hold mode: 1-second press-and-hold to open
              the type picker; never sends the alert directly). */}
        <section className="flex flex-col items-center pt-1 -mx-3" data-testid="sos-section">
          <SosButton
            onConfirm={handleSosTap}
            disabled={submitting}
            mode="hold"
          />
        </section>

        {/* 3 — Tutorial CTA (compact, always visible on idle). */}
        <button
          type="button"
          onClick={() => setTutorialOpen(true)}
          data-testid="open-tutorial-btn"
          className="flex items-center justify-between gap-3 bg-card/70 border border-dashed border-border rounded-2xl px-4 py-2.5 hover:bg-card hover:border-mali-navy/40 active:scale-[0.99] transition-all"
        >
            <div className="flex items-center gap-2.5 min-w-0">
              <span className="w-8 h-8 rounded-lg bg-mali-yellow/20 text-amber-700 dark:text-mali-yellow flex items-center justify-center shrink-0">
                <PlayCircle className="w-4 h-4" strokeWidth={2.5} />
              </span>
              <div className="text-left min-w-0">
                <div className="text-[12px] font-bold text-mali-navy dark:text-white leading-tight">
                  {t.tutorialCtaTitle}
                </div>
                <div className="text-[10px] text-muted-foreground leading-tight mt-0.5">
                  {t.tutorialCtaSubtitle}
                </div>
              </div>
            </div>
            <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
          </button>

        {/* 5 — Secure footer */}
        <div
          className="mt-auto flex items-center justify-center gap-2 text-[11px] text-muted-foreground"
          data-testid="secure-footer"
        >
          <Lock className="w-3.5 h-3.5 text-emerald-600" strokeWidth={2.5} />
          <span>{t.secureFooter}</span>
        </div>
      </main>

      <HomeTypeDrawer
        open={flowStep === "pickType"}
        onOpenChange={(open) => !open && setFlowStep("idle")}
        types={types}
        onTypesChange={setTypes}
        onContinue={onTypeContinue}
        t={t}
      />

      <HomeEvidenceDrawer
        open={flowStep === "pickEvidence"}
        onOpenChange={(open) => !open && setFlowStep("pickType")}
        attachments={attachments}
        onAttachmentsChange={setAttachments}
        description={description}
        onDescriptionChange={setDescription}
        onBack={() => setFlowStep("pickType")}
        onContinue={onEvidenceContinue}
        t={t}
      />

      <HomeReviewScreen
        open={flowStep === "review"}
        onBack={() => setFlowStep("pickEvidence")}
        onSend={dispatchAlert}
        types={types}
        typeLabels={labels}
        attachments={attachments}
        coords={reportFrom || coords}
        submitting={submitting}
      />

      <AlertResultModal alert={result} onClose={() => setResult(null)} />
      <TutorialModal open={tutorialOpen} onClose={handleTutorialClose} />
    </div>
  );
}
